/**
 * Auth Context
 * STORY-016A: Context Menu Core Navigation
 *
 * Provides authentication state and permission checking throughout the application.
 * Integrates with authService for user data, roles, and permissions via GET /auth/me.
 *
 * @example
 * ```tsx
 * function ProtectedComponent() {
 *   const { user, hasPermission, isAuthenticated } = useAuth();
 *
 *   if (!isAuthenticated) return <Navigate to="/login" />;
 *   if (!hasPermission('users.read')) return <AccessDenied />;
 *
 *   return <UsersList />;
 * }
 * ```
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { authService, LoginRequest, LoginResponse, AUTH_STATE_CHANGE_EVENT } from '../services/authService';
import { Role } from '../services/rolesService';

/**
 * Token validation interval in milliseconds (30 seconds)
 * This ensures we catch expired tokens reasonably quickly
 * STORY-001: Increased to reduce validation frequency and race conditions
 */
const TOKEN_VALIDATION_INTERVAL = 45 * 1000;

/**
 * Proactive refresh buffer in seconds (90 seconds before expiration)
 * Refresh token this many seconds before it actually expires
 * STORY-001: Increased buffer to allow more time for proactive refresh
 */
const PROACTIVE_REFRESH_BUFFER = 90;

/**
 * Minimum time between proactive refresh attempts (milliseconds)
 * STORY-001: Debounce to prevent rapid refresh attempts
 */
const MIN_PROACTIVE_REFRESH_INTERVAL = 30 * 1000;

/**
 * User interface for authenticated user
 */
export interface User {
  id: number;
  email: string;
  name: string;
  status: string;
  role?: Role;
  permissions?: string[];
  organization_id?: number;
}

/**
 * Auth context state
 */
export interface AuthContextState {
  /** Currently authenticated user */
  user: User | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth state is still loading */
  isLoading: boolean;
  /** User's permissions (permission names) */
  permissions: string[];
  /** User's role */
  role: Role | null;
  /** Check if user has a specific permission */
  hasPermission: (permission: string) => boolean;
  /** Check if user has any of the specified permissions */
  hasAnyPermission: (permissions: string[]) => boolean;
  /** Check if user has all of the specified permissions */
  hasAllPermissions: (permissions: string[]) => boolean;
  /** Login user */
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  /** Logout user */
  logout: () => Promise<void>;
  /** Refresh user data */
  refreshUser: () => Promise<void>;
}

/**
 * Default context value
 */
const defaultContextValue: AuthContextState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  permissions: [],
  role: null,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  login: async () => {
    throw new Error('AuthContext not initialized');
  },
  logout: async () => {
    throw new Error('AuthContext not initialized');
  },
  refreshUser: async () => {
    throw new Error('AuthContext not initialized');
  },
};

/**
 * Auth Context
 */
export const AuthContext = createContext<AuthContextState>(defaultContextValue);

/**
 * Storage key for persisted user data
 */
const USER_STORAGE_KEY = 'auth_user';
const PERMISSIONS_STORAGE_KEY = 'auth_permissions';

/**
 * Auth Provider Props
 */
export interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Auth Provider Component
 *
 * Wraps the application to provide authentication state and methods.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [role, setRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Ref to track if we're currently refreshing to prevent multiple refresh attempts
  const isRefreshingRef = useRef(false);

  // STORY-001: Track last proactive refresh attempt timestamp
  const lastProactiveRefreshRef = useRef(0);

  /**
   * Load user from storage on mount
   * STORY-008: Enhanced to recover session from refresh token even if auth_user is missing
   */
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        // Check if we have a stored user
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);
        const storedPermissions = localStorage.getItem(PERMISSIONS_STORAGE_KEY);

        if (storedUser && authService.isAuthenticated()) {
          // Normal case: both user data and tokens exist
          const userData = JSON.parse(storedUser) as User;
          setUser(userData);

          if (storedPermissions) {
            setPermissions(JSON.parse(storedPermissions));
          }

          // If the access token is expired but refresh token is still valid,
          // proactively refresh the token BEFORE any API calls.
          // This prevents race conditions where multiple concurrent API calls
          // each trigger 401 → refresh, causing token rotation reuse detection
          // which revokes all tokens and forces a double login.
          if (authService.isTokenAboutToExpire(0)) {
            try {
              console.debug('Session restore: Access token expired, refreshing before API calls...');
              await authService.refreshToken();
              console.debug('Session restore: Token refreshed successfully');
            } catch (refreshError) {
              // Refresh failed - tokens are invalid, clear everything
              console.warn('Session restore: Token refresh failed, clearing session');
              authService.clearTokens();
              setUser(null);
              setPermissions([]);
              setRole(null);
              localStorage.removeItem(USER_STORAGE_KEY);
              localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
              setIsLoading(false);
              return;
            }
          }

          // Try to refresh user data in background
          // Don't await to avoid blocking the UI
          refreshUserData().catch(console.error);
        } else if (!storedUser && authService.isAuthenticated()) {
          // Recovery case: refresh token exists but user data is missing
          // This can happen when browser clears some localStorage but keeps tokens
          // Try to recover the session by fetching the current user's data
          console.log('Attempting to recover session from refresh token...');
          try {
            const currentUser = await authService.getCurrentUser();

            const recoveredUser: User = {
              id: currentUser.id,
              email: currentUser.email,
              name: currentUser.name,
              status: currentUser.status,
            };

            setUser(recoveredUser);
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(recoveredUser));

            // Set permissions from the user's actual roles
            setPermissions(currentUser.permissions);
            localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(currentUser.permissions));

            if (currentUser.roles && currentUser.roles.length > 0) {
              setRole({
                id: currentUser.roles[0].id,
                name: currentUser.roles[0].name,
                description: currentUser.roles[0].description,
                created_at: '',
              } as Role);
            }

            console.log('Session recovered successfully');
          } catch (refreshError) {
            // Token refresh failed - user needs to login again
            console.log('Session recovery failed, user needs to login');
            authService.clearTokens();
          }
        }
      } catch (error) {
        console.error('Error loading stored auth:', error);
        // Clear invalid stored data
        localStorage.removeItem(USER_STORAGE_KEY);
        localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  /**
   * Handle logout - clears user state and storage
   */
  const handleLogout = useCallback(() => {
    setUser(null);
    setPermissions([]);
    setRole(null);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
  }, []);

  /**
   * Proactive token refresh
   * Refreshes the token before it expires to prevent session interruption
   * STORY-001: Enhanced with debouncing and coordination with authService
   */
  const proactiveTokenRefresh = useCallback(async () => {
    // STORY-001: Check if authService is already refreshing
    if (isRefreshingRef.current || authService.isRefreshInProgress()) {
      console.debug('Skipping proactive refresh: refresh already in progress');
      return;
    }

    // STORY-001: Debounce rapid refresh attempts
    const now = Date.now();
    if (now - lastProactiveRefreshRef.current < MIN_PROACTIVE_REFRESH_INTERVAL) {
      console.debug('Skipping proactive refresh: too soon since last attempt');
      return;
    }

    try {
      isRefreshingRef.current = true;
      lastProactiveRefreshRef.current = now;
      await authService.refreshToken();
      console.debug('Proactive token refresh successful');
    } catch (error) {
      console.warn('Proactive token refresh failed:', error);
      // STORY-001: Don't force logout here - let the interceptor handle it on the next API call
      // The interceptor has better retry logic and coordination
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  /**
   * Periodic token validation
   * Checks token status every 45 seconds and handles expiration
   * IMPORTANT: This prevents users from working with expired sessions
   * STORY-001: Enhanced to avoid race conditions with interceptor refresh
   */
  useEffect(() => {
    // Don't run if not authenticated or still loading
    if (isLoading || !user) return;

    const validateToken = async () => {
      // STORY-001: Skip validation if a refresh is already in progress
      if (authService.isRefreshInProgress() || isRefreshingRef.current) {
        console.debug('Token validation: Skipping - refresh in progress');
        return;
      }

      // Check if token is still valid (with 0 buffer - only check if actually expired)
      // STORY-001: Only force logout if BOTH access and refresh tokens are truly expired
      const isAuth = authService.isAuthenticated();
      if (!isAuth) {
        // STORY-001: Double-check before forcing logout to prevent false positives
        // Give a small delay to allow any pending refresh to complete
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check again after delay
        if (!authService.isAuthenticated() && !authService.isRefreshInProgress()) {
          console.warn('Token validation: Session expired, logging out');
          handleLogout();
          authService.forceLogout('session_expired');
          return;
        }
        // If authenticated now or refresh in progress, don't logout
        console.debug('Token validation: Session recovered during grace period');
        return;
      }

      // Check if token is about to expire and proactively refresh
      // STORY-001: Use the time-until-expiration to be more precise
      const timeUntilExpiry = authService.getTimeUntilExpiration();
      const shouldRefresh = timeUntilExpiry > 0 && timeUntilExpiry <= PROACTIVE_REFRESH_BUFFER * 1000;

      if (shouldRefresh) {
        console.debug(`Token validation: Token expires in ${Math.round(timeUntilExpiry / 1000)}s, refreshing proactively`);
        await proactiveTokenRefresh();
      }
    };

    // Run immediately on mount (with a small delay to let initial state settle)
    const initialTimeout = setTimeout(validateToken, 2000);

    // Set up periodic validation
    const intervalId = setInterval(validateToken, TOKEN_VALIDATION_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
    };
  }, [isLoading, user, handleLogout, proactiveTokenRefresh]);

  /**
   * Listen for auth state change events from authService
   * This handles cases where the interceptor detects token expiration
   * STORY-001: Enhanced with debouncing to prevent multiple rapid logouts
   */
  useEffect(() => {
    let logoutDebounceTimer: NodeJS.Timeout | null = null;

    const handleAuthStateChange = (event: CustomEvent<{ isAuthenticated: boolean; reason: string }>) => {
      const { isAuthenticated, reason } = event.detail;

      if (!isAuthenticated) {
        console.warn(`Auth state change event: ${reason}`);

        // STORY-001: Debounce logout to prevent multiple rapid events
        if (logoutDebounceTimer) {
          clearTimeout(logoutDebounceTimer);
        }

        logoutDebounceTimer = setTimeout(() => {
          // Only logout if we're still not authenticated after debounce
          if (!authService.isAuthenticated()) {
            handleLogout();
          }
        }, 500);
      }
    };

    window.addEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange as EventListener);

    return () => {
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange as EventListener);
      if (logoutDebounceTimer) {
        clearTimeout(logoutDebounceTimer);
      }
    };
  }, [handleLogout]);

  /**
   * Refresh user data from server
   * Fetches the current user's actual roles and permissions via GET /auth/me
   */
  const refreshUserData = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();

      const enrichedUserData: User = {
        id: currentUser.id,
        email: currentUser.email,
        name: currentUser.name,
        status: currentUser.status,
        organization_id: currentUser.organization_id,
      };

      setUser(enrichedUserData);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(enrichedUserData));

      setPermissions(currentUser.permissions);
      localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(currentUser.permissions));

      if (currentUser.roles && currentUser.roles.length > 0) {
        setRole({
          id: currentUser.roles[0].id,
          name: currentUser.roles[0].name,
          description: currentUser.roles[0].description,
          created_at: '',
        } as Role);
      }
    } catch (error) {
      // Silently fail - will retry on next interval
      console.debug('Could not refresh user data:', error);
    }
  }, []);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      // Null permission means public access
      if (permission === null || permission === undefined || permission === '') {
        return true;
      }
      // Check if user has the exact permission
      if (permissions.includes(permission)) {
        return true;
      }
      // Check for wildcard permissions (e.g., 'users.*' grants all users permissions)
      const [category] = permission.split('.');
      if (permissions.includes(`${category}.*`)) {
        return true;
      }
      // Check for super admin permission
      if (permissions.includes('*') || permissions.includes('admin.*')) {
        return true;
      }
      return false;
    },
    [permissions]
  );

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (permissionList: string[]): boolean => {
      return permissionList.some((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = useCallback(
    (permissionList: string[]): boolean => {
      return permissionList.every((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  /**
   * Login user
   * CA-1: Enhanced error handling and MFA response support
   */
  const login = useCallback(async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await authService.login(credentials);

    // CA-1: Check if response contains user data (MFA responses don't have user)
    if (!response.user) {
      // MFA required - return response without setting user state
      // The MFA flow will handle this separately
      return response;
    }

    // Create user object from response
    const userData: User = {
      id: response.user.id,
      email: response.user.email,
      name: response.user.name,
      status: response.user.status,
      organization_id: response.user.organization_id,
    };

    setUser(userData);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));

    // CA-1: Small delay to ensure token is properly stored before API calls
    // This fixes a race condition where subsequent API calls may not have the token
    await new Promise(resolve => setTimeout(resolve, 50));

    // Fetch the user's actual permissions via GET /auth/me
    try {
      const currentUser = await authService.getCurrentUser();

      // Set permissions from the user's actual roles
      setPermissions(currentUser.permissions);
      localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(currentUser.permissions));

      // Set role from user's actual assigned role
      if (currentUser.roles && currentUser.roles.length > 0) {
        setRole({
          id: currentUser.roles[0].id,
          name: currentUser.roles[0].name,
          description: currentUser.roles[0].description,
          created_at: '',
        } as Role);
      }
    } catch (error) {
      console.warn('Could not fetch user permissions:', error);
      // Safe default: no permissions (user will only see public menus)
      setPermissions([]);
      localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify([]));
    }

    return response;
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async (): Promise<void> => {
    await authService.logout();
    handleLogout();
  }, [handleLogout]);

  /**
   * Memoized context value
   */
  const contextValue = useMemo<AuthContextState>(
    () => ({
      user,
      isAuthenticated: !!user && authService.isAuthenticated(),
      isLoading,
      permissions,
      role,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      login,
      logout,
      refreshUser: refreshUserData,
    }),
    [
      user,
      isLoading,
      permissions,
      role,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      login,
      logout,
      refreshUserData,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 *
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to check a single permission
 *
 * @param permission - Permission to check (null for public access)
 * @returns boolean indicating if user has the permission
 *
 * @example
 * ```tsx
 * function DeleteButton() {
 *   const canDelete = usePermission('users.delete');
 *   if (!canDelete) return null;
 *   return <button>Delete</button>;
 * }
 * ```
 */
export function usePermission(permission: string | null): boolean {
  const { hasPermission } = useAuth();
  return permission === null ? true : hasPermission(permission);
}

export default AuthContext;
